import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/primitives";
import { toast } from "@/components/ui/sonner";
import { useDecide } from "./api";

export interface Decision {
  /** Approval request id. */
  id: string;
  title: string;
  requestedBy?: string | null;
  decision: "approve" | "reject";
}

/** Approve (optional comment) or reject (reason required) one approval. */
export function DecideDialog({ decision, onClose }: { decision: Decision; onClose: () => void }) {
  const [comment, setComment] = useState("");
  const decide = useDecide();
  const rejecting = decision.decision === "reject";
  const missingReason = rejecting && !comment.trim();

  const submit = () =>
    decide.mutate(
      { id: decision.id, decision: decision.decision, comment: comment.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(rejecting ? "Rejected" : "Approved");
          onClose();
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't save the decision."),
      },
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{rejecting ? "Reject request" : "Approve request"}</DialogTitle>
          <DialogDescription>{decision.title}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="approval-comment">{rejecting ? "Reason (required)" : "Comment (optional)"}</Label>
          <Textarea
            id="approval-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={4000}
            rows={3}
            placeholder={rejecting ? "Tell them why, so they can rearrange" : "Anything they should know"}
          />
          <p className="text-xs text-muted-foreground">{decision.requestedBy ?? "The requester"} sees this in Pulse.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant={rejecting ? "destructive" : "default"} disabled={missingReason || decide.isPending} onClick={submit}>
            {decide.isPending ? "Saving…" : rejecting ? "Reject" : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
